import * as THREE from 'three';

export interface WallSurfaceConfig {
  width: number;
  height: number;
  position: readonly number[]; // [x, y, z]
  rotation: readonly number[]; // [x, y, z] (rad)
  seed: readonly number[]; // [x, y] 노이즈 오프셋
  vertexShader: string;
  fragmentShader: string;
}

/**
 * 방의 한 면(평면). 자기만의 ShaderMaterial을 가져 morph·명화·페이드를 독립 제어한다.
 * 6개를 모으면 방이 되고, 피날레에선 면마다 다른 명화를 띄울 수 있다.
 */
export class WallSurface {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor(cfg: WallSurfaceConfig) {
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMorph: { value: 0 },
        uHasPainting: { value: 0 },
        uPainting: { value: createPlaceholderTexture() },
        uSeed: { value: new THREE.Vector2(cfg.seed[0], cfg.seed[1]) },
        uFade: { value: 1 },
      },
      vertexShader: cfg.vertexShader,
      fragmentShader: cfg.fragmentShader,
    });

    const geometry = new THREE.PlaneGeometry(cfg.width, cfg.height);
    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.position.set(cfg.position[0], cfg.position[1], cfg.position[2]);
    this.mesh.rotation.set(cfg.rotation[0], cfg.rotation[1], cfg.rotation[2]);
  }

  setMorph(value: number): void {
    this.material.uniforms.uMorph.value = value;
  }

  setFade(value: number): void {
    this.material.uniforms.uFade.value = value;
  }

  /** 로드된 텍스처만 바인딩한다. 미로드 시 플레이스홀더 유지 + 명화 비표시. */
  setPainting(texture: THREE.Texture, loaded: boolean): void {
    if (loaded) {
      this.material.uniforms.uPainting.value = texture;
      this.material.uniforms.uHasPainting.value = 1;
    } else {
      this.material.uniforms.uHasPainting.value = 0;
    }
  }

  update(dt: number): void {
    this.material.uniforms.uTime.value += dt;
  }
}

/** 텍스처 로드 전 sampler2D에 바인딩할 1x1 검정 플레이스홀더 (no-texture 경고 방지). */
function createPlaceholderTexture(): THREE.DataTexture {
  const texture = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
  texture.needsUpdate = true;
  return texture;
}
